import { type } from 'arktype';

export const personDepartmentRoleSchema = type("'lead' | 'deputy_lead'").configure({
  description: 'Role the person holds within the department.',
  examples: ['lead', 'deputy_lead'],
});

export type PersonDepartmentRole = typeof personDepartmentRoleSchema.t;

const personDepartmentBase = {
  personId: type('string').configure({
    description: 'ID of the person being assigned to the department.',
    examples: ['5a5e8d80-1b2c-4e7a-8f9d-0a1b2c3d4e5f'],
  }),
  departmentId: type('string').configure({
    description: 'ID of the department the person is joining.',
    examples: ['dept_operations'],
  }),
  role: personDepartmentRoleSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment starts. Required for deputy_lead; optional otherwise.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment ends. Required for deputy_lead; optional otherwise.',
    examples: ['2026-12-31'],
  }),
};

function assertDeputyDates(value: { role: string; startDate?: string | null; endDate?: string | null }, ctx: object): boolean {
  if (value.role !== 'deputy_lead') return true;
  const hasStart = typeof value.startDate === 'string' && value.startDate.length > 0;
  const hasEnd = typeof value.endDate === 'string' && value.endDate.length > 0;
  return (
    (hasStart && hasEnd) ||
    (ctx as { mustBe: (msg: string) => boolean }).mustBe('deputy_lead assignments require both startDate and endDate')
  );
}

export const createPersonDepartmentSchema = type(personDepartmentBase)
  .narrow(assertDeputyDates)
  .configure({ description: 'Payload to assign a person to a department with an optional validity period.' });

export type CreatePersonDepartment = typeof createPersonDepartmentSchema.t;

export const updatePersonDepartmentSchema = type({
  'personId?': type('string').configure({
    description: 'Updated person ID. Optional — rarely changed after creation.',
  }),
  'departmentId?': type('string').configure({
    description: 'Updated department ID. Optional — rarely changed after creation.',
  }),
  'role?': personDepartmentRoleSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'Updated assignment start date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Updated assignment end date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
}).configure({
  description:
    'Partial update payload for an existing person-department assignment. Deputy date rules apply to the merged row and are enforced service-side.',
});

export type UpdatePersonDepartment = typeof updatePersonDepartmentSchema.t;
